import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  LifeBuoy,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sun,
  UserRoundCheck,
  X,
} from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { get, type ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { useSession } from '../lib/session';
import { useTheme, type Theme } from '../lib/theme';
import { Avatar, Badge, Button, ErrorState, Spinner } from '../components/ui';
import { Dialog } from '../components/dialog';
import { describeError, useToast } from '../components/toast';
import { SideSheet, SideSheetClose } from '../components/side-sheet';
import { NotificationBell } from '../features/notifications/notification-bell';
import { AvailabilityDialog } from '../features/people/person-availability';
import { peopleKeys } from '../features/people/cache';
import type { ProfileResponse } from '../features/people/types';
import { isNavItemActive, navFor, visibleCreateActions, visibleNav, type NavGroup } from '../features/navigation/nav-items';
import { CommandPalette, PaletteContext, isMac, usePaletteShortcut } from '../features/search/command-palette';

/**
 * The application shell.
 *
 * A permanent left rail on desktop and a side sheet below `lg`. The
 * navigation is filtered by permission, so an agent without change management
 * never sees a link that would only refuse them — hiding it is kinder than a
 * 403, and the server refuses regardless.
 *
 * Agents and requesters get different maps. A requester used to see the agent
 * console with pieces missing — "Assigned to me", an "Unassigned" filter, SLA
 * timers — which is a tool for somebody else's job. They now see the things
 * they come here to do, in their own words.
 */

const MENU_CONTENT =
  'animate-pop z-50 rounded-card border border-line bg-raised p-1 shadow-float';
const MENU_ITEM =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken';

/** The width at which the rail is permanent (Tailwind's `lg`). */
const DESKTOP_QUERY = '(min-width: 1024px)';

function useMediaQuery(query: string): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [matches, setMatches] = useState(() => (supported ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!supported) return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query, supported]);
  return matches;
}

export function AppShell() {
  const { session, can } = useSession();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const desktop = useMediaQuery(DESKTOP_QUERY);

  // Navigating, or widening the window until the rail is permanent, dismisses
  // the sheet; leaving it open over the new page makes the app feel broken.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, desktop]);

  const togglePalette = useCallback((update: (open: boolean) => boolean) => setPaletteOpen(update), []);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  usePaletteShortcut(togglePalette);

  /**
   * How many decisions are waiting, for the badge beside Approvals. The same
   * query the approvals page and the requester home read, so a decision made
   * on either clears it.
   */
  const approvals = useQuery<{ data: unknown[] }, ApiError>({
    queryKey: ['approvals', 'mine'],
    queryFn: () => get('/approvals/mine'),
    enabled: can('approval.view'),
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });

  if (!session) return null;

  const groups = visibleNav(navFor(session.is_agent), can);
  const counts: Record<string, number> = { '/approvals': approvals.data?.data.length ?? 0 };

  return (
    <div className="flex h-full bg-canvas">
      <a
        href="#main"
        className="sr-only-focusable fixed top-3 start-3 z-50 rounded-control bg-accent px-3 py-2 text-mini font-medium text-white"
      >
        Skip to content
      </a>

      {/* --- Sidebar: permanent from lg up ------------------------------- */}
      <nav
        aria-label="Main"
        className="hidden w-60 shrink-0 flex-col border-e border-line bg-surface lg:flex"
      >
        <SidebarContent groups={groups} counts={counts} />
      </nav>

      {/* --- Sidebar: a sheet below lg ------------------------------------ */}
      {!desktop && (
        <SideSheet open={drawerOpen} onOpenChange={setDrawerOpen} title="Navigation">
          <nav aria-label="Main" className="flex min-h-0 flex-1 flex-col">
            <SidebarContent groups={groups} counts={counts} closable />
          </nav>
        </SideSheet>
      )}

      {/* --- Main ------------------------------------------------------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenDrawer={() => setDrawerOpen(true)} onOpenSearch={openPalette} />
        {session.impersonated_by !== null && <ImpersonationBanner />}
        <main id="main" className="min-h-0 flex-1 overflow-hidden">
          <PaletteContext.Provider value={openPalette}>
            {/* Pages that load on demand show this while their code arrives,
                with the navigation still in place around them. */}
            <Suspense fallback={<PageLoading />}>
              <Outlet />
            </Suspense>
          </PaletteContext.Provider>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

function SidebarContent({
  groups,
  counts,
  closable = false,
}: {
  groups: NavGroup[];
  counts: Record<string, number>;
  closable?: boolean;
}) {
  return (
    <>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        <Link to="/" className="flex items-center gap-2 rounded-control">
          <span className="flex size-6 items-center justify-center rounded bg-accent text-white">
            <LifeBuoy aria-hidden className="size-3.5" />
          </span>
          <span className="text-body font-semibold tracking-tight text-ink">Aurora</span>
        </Link>
        {closable && (
          <SideSheetClose asChild>
            <Button size="icon" variant="ghost" className="ms-auto" aria-label="Close navigation">
              <X aria-hidden className="size-4" />
            </Button>
          </SideSheetClose>
        )}
      </div>

      <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto px-2 py-3">
        <NavGroups groups={groups} counts={counts} />
      </div>

      <WorkspaceFooter />
    </>
  );
}

function NavGroups({ groups, counts }: { groups: NavGroup[]; counts: Record<string, number> }) {
  const { pathname } = useLocation();
  return (
    <div className="space-y-4">
      {groups.map((group, index) => (
        <div key={group.label ?? index}>
          {group.label && (
            <p className="px-2 pb-1 text-mini font-semibold text-ink-subtle">{group.label}</p>
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const count = counts[item.to] ?? 0;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    aria-current={isNavItemActive(item, pathname) ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-2.5 rounded-control px-2 py-1.5 text-body transition-colors',
                      isNavItemActive(item, pathname)
                        ? 'bg-accent-soft font-medium text-accent-ink'
                        : 'text-ink-muted hover:bg-sunken hover:text-ink',
                    )}
                  >
                    <item.icon aria-hidden className="size-4 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {count > 0 && (
                      <span className="rounded-full bg-caution-soft px-1.5 text-mini font-medium text-caution-ink">
                        {count}
                        <span className="sr-only-focusable"> waiting</span>
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * Which workspace this is.
 *
 * This used to be a dropdown listing every workspace, and choosing one did
 * nothing: there is no endpoint that switches workspace, and each request is
 * scoped on the server from the session. A control that looks like it changes
 * something and does not is worse than no control, so it is a label — and
 * only when there is more than one workspace to tell apart.
 */
function WorkspaceFooter() {
  const { session } = useSession();
  if (!session || session.workspaces.length <= 1) return null;

  const current = session.workspaces.find((w) => w.is_default) ?? session.workspaces[0]!;
  const others = session.workspaces.length - 1;

  return (
    <div className="flex shrink-0 items-center gap-2 border-t border-line px-4 py-2.5">
      <span className="flex size-5 shrink-0 items-center justify-center rounded bg-sunken text-mini font-semibold text-ink-muted uppercase">
        {current.key.slice(0, 2)}
      </span>
      <span className="min-w-0 flex-1 truncate text-mini text-ink-muted" title={current.name}>
        {current.name}
        <span className="text-ink-subtle"> · +{others}</span>
      </span>
    </div>
  );
}

const THEMES: ReadonlyArray<readonly [Theme, string, typeof Sun]> = [
  ['light', 'Light', Sun],
  ['dark', 'Dark', Moon],
  ['system', 'Match my device', Monitor],
];

function TopBar({ onOpenDrawer, onOpenSearch }: { onOpenDrawer: () => void; onOpenSearch: () => void }) {
  const { session, can, signOut } = useSession();
  const { theme, setTheme } = useTheme();
  const toast = useToast();
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const availabilityWasOpen = useRef(false);

  /**
   * Back to the account menu's button once the availability dialog has gone.
   *
   * A dialog hands focus back to whatever had it when it opened, which here
   * was a menu item that went with the menu — so focus fell to the page itself
   * and a keyboard user started again from the top. The dialog's own attempt
   * is queued from a cleanup that runs just before this effect, so this one,
   * queued after it, has the last word.
   */
  useEffect(() => {
    if (availabilityOpen) {
      availabilityWasOpen.current = true;
      return;
    }
    if (!availabilityWasOpen.current) return;
    availabilityWasOpen.current = false;
    const timer = window.setTimeout(() => accountButtonRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [availabilityOpen]);

  if (!session) return null;

  const creates = visibleCreateActions(session.is_agent, can);
  const ticketAction = creates.find((action) => action.to === '/tickets/new');
  const otherCreates = creates.filter((action) => action !== ticketAction);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      toast.error('Couldn’t sign you out', describeError(error).title);
    }
  };

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
      <Button size="icon" variant="ghost" className="lg:hidden" onClick={onOpenDrawer} aria-label="Open navigation">
        <Menu aria-hidden className="size-4" />
      </Button>

      {/* Looks like a search box because that is what people reach for; opens
          the palette, which is where searching actually happens. */}
      <button
        type="button"
        onClick={onOpenSearch}
        aria-label="Search"
        aria-keyshortcuts={isMac() ? 'Meta+K' : 'Control+K'}
        className={cn(
          'flex h-8 min-w-0 items-center gap-2 rounded-control border border-line-strong bg-sunken px-2.5 text-body text-ink-subtle',
          'transition-colors hover:border-ink-faint hover:text-ink-muted',
          'w-8 justify-center sm:w-full sm:max-w-md sm:flex-1 sm:justify-start',
        )}
      >
        <Search aria-hidden className="size-3.5 shrink-0" />
        <span className="hidden truncate sm:inline">
          {session.is_agent ? 'Search tickets, people, assets, articles…' : 'Search for help or a service…'}
        </span>
        <kbd className="ms-auto hidden shrink-0 rounded border border-line bg-surface px-1.5 font-sans text-mini text-ink-subtle md:inline">
          {isMac() ? '⌘' : 'Ctrl'} K
        </kbd>
      </button>

      <div className="ms-auto flex items-center gap-1">
        {session.is_agent ? (
          <div className="me-1 flex items-center">
            {/* The thing an agent creates all day is one click; everything
                else is behind the chevron. */}
            {ticketAction && (
              <Button
                asChild
                variant="primary"
                size="sm"
                className={cn(otherCreates.length > 0 && 'rounded-e-none')}
              >
                <Link to={ticketAction.to}>
                  <Plus aria-hidden className="size-3.5" />
                  <span className="hidden sm:inline">New ticket</span>
                  <span className="sr-only-focusable sm:hidden">New ticket</span>
                </Link>
              </Button>
            )}
            {otherCreates.length > 0 && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <Button
                    variant="primary"
                    size="sm"
                    aria-label={ticketAction ? 'Create something else' : 'Create'}
                    className={cn(ticketAction && 'rounded-s-none border-s border-white/25 px-1.5')}
                  >
                    {!ticketAction && (
                      <>
                        <Plus aria-hidden className="size-3.5" />
                        New
                      </>
                    )}
                    <ChevronDown aria-hidden className="size-3.5 opacity-90" />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={6} className={cn(MENU_CONTENT, 'min-w-48')}>
                    {otherCreates.map((action) => (
                      <DropdownMenu.Item key={action.to} asChild className={MENU_ITEM}>
                        <Link to={action.to}>
                          <action.icon aria-hidden className="size-3.5 text-ink-subtle" />
                          New {action.label.toLowerCase()}
                        </Link>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}
          </div>
        ) : (
          can('ticket.create') && (
            <Button asChild variant="primary" size="sm" className="me-1">
              <Link to="/tickets/new">
                <Plus aria-hidden className="size-3.5" />
                <span className="hidden sm:inline">Report an issue</span>
                <span className="sm:hidden">Report</span>
              </Link>
            </Button>
          )
        )}

        <NotificationBell />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              ref={accountButtonRef}
              type="button"
              className="flex items-center gap-2 rounded-control py-1 pe-2 ps-1 transition-colors hover:bg-sunken"
              aria-label={`Account menu for ${session.user.name}`}
            >
              <Avatar name={session.user.name} size="sm" />
              <span dir="auto" className="hidden max-w-32 truncate text-mini text-ink sm:inline">
                {session.user.name}
              </span>
              <ChevronDown aria-hidden className="size-3 text-ink-subtle" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="end" sideOffset={6} className={cn(MENU_CONTENT, 'w-64')}>
              <div className="px-2 py-2">
                <p dir="auto" className="truncate text-body font-medium text-ink">
                  {session.user.name}
                </p>
                <p className="truncate text-mini text-ink-muted">{session.user.email}</p>
                <Badge tone={session.is_agent ? 'accent' : 'neutral'} className="mt-1.5">
                  {session.is_agent ? 'Agent' : 'Requester'}
                </Badge>
              </div>
              <DropdownMenu.Separator className="my-1 h-px bg-line" />
              {session.is_agent && (
                <DropdownMenu.Item onSelect={() => setAvailabilityOpen(true)} className={MENU_ITEM}>
                  <UserRoundCheck aria-hidden className="size-3.5" />
                  My availability
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item asChild className={MENU_ITEM}>
                <Link to="/security">
                  <ShieldCheck aria-hidden className="size-3.5" />
                  Sign-in &amp; security
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-line" />
              <DropdownMenu.Label className="px-2 pt-1 pb-0.5 text-mini font-medium text-ink-muted">
                Theme
              </DropdownMenu.Label>
              {/* A radio group, so the current choice is announced and ticked,
                  not merely tinted. */}
              <DropdownMenu.RadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                {THEMES.map(([value, label, Icon]) => (
                  <DropdownMenu.RadioItem key={value} value={value} className={cn(MENU_ITEM, 'relative ps-8')}>
                    <DropdownMenu.ItemIndicator className="absolute start-2 flex items-center text-accent-ink">
                      <Check aria-hidden className="size-3.5" />
                    </DropdownMenu.ItemIndicator>
                    <Icon aria-hidden className="size-3.5 text-ink-subtle" />
                    {label}
                  </DropdownMenu.RadioItem>
                ))}
              </DropdownMenu.RadioGroup>

              <DropdownMenu.Separator className="my-1 h-px bg-line" />
              <p className="px-2 py-1.5 text-mini text-ink-muted">
                Search from anywhere with{' '}
                <kbd className="rounded border border-line bg-sunken px-1 font-sans">{isMac() ? '⌘' : 'Ctrl'} K</kbd> or{' '}
                <kbd className="rounded border border-line bg-sunken px-1 font-sans">/</kbd>
              </p>

              <DropdownMenu.Separator className="my-1 h-px bg-line" />
              <DropdownMenu.Item onSelect={() => void handleSignOut()} className={MENU_ITEM}>
                <LogOut aria-hidden className="size-3.5 rtl:rotate-180" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        {/* Outside the menu, which unmounts its contents as it closes. */}
        {session.is_agent && availabilityOpen && (
          <MyAvailabilityDialog agentId={session.user.id} onClose={() => setAvailabilityOpen(false)} />
        )}
      </div>
    </header>
  );
}

/**
 * An agent marking themselves away — before lunch, before leave — from
 * wherever they are, not only from the People screen.
 *
 * The form starts from what it is given and does not follow later changes, so
 * it is given the state as it is now, fetched as the dialog opens, rather than
 * a cached copy a supervisor may have changed since.
 */
function MyAvailabilityDialog({ agentId, onClose }: { agentId: number; onClose: () => void }) {
  const profile = useQuery<ProfileResponse, ApiError>({
    queryKey: peopleKeys.profile(agentId),
    queryFn: () => get(`/users/${agentId}/profile`),
    refetchOnMount: 'always',
  });
  const onOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  if (profile.isSuccess && profile.isFetchedAfterMount) {
    return (
      <AvailabilityDialog
        open
        onOpenChange={onOpenChange}
        agentId={agentId}
        name={profile.data.data.full_name}
        initial={profile.data.data}
        isSelf
      />
    );
  }

  return (
    <Dialog open onOpenChange={onOpenChange} title="Your availability" size="sm">
      {profile.isError && !profile.isFetching ? (
        <ErrorState
          title="Couldn’t load your availability"
          message={profile.error.message}
          requestId={profile.error.requestId}
          onRetry={() => void profile.refetch()}
        />
      ) : (
        <Spinner label="Loading your availability" />
      )}
    </Dialog>
  );
}

/**
 * Impersonation is never invisible, and never a trap.
 *
 * An administrator acting as someone else sees a persistent banner naming who
 * they are acting as, and every write they make is attributed to both of them
 * in the audit log. Supporting a user is legitimate; doing it without a trace
 * is not. The way back is on the banner itself: it used to be signing out and
 * signing in again.
 */
function ImpersonationBanner() {
  const { session, stopImpersonating } = useSession();
  const toast = useToast();
  const [returning, setReturning] = useState(false);
  if (!session) return null;

  const returnToOwnAccount = async () => {
    setReturning(true);
    try {
      await stopImpersonating();
    } catch (error) {
      toast.error('Couldn’t return to your account', describeError(error).title);
      setReturning(false);
    }
  };

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-caution-line bg-caution-soft px-4 py-2 sm:px-6"
    >
      <ShieldAlert aria-hidden className="size-4 shrink-0 text-caution-ink" />
      <p className="min-w-0 flex-1 text-body text-caution-ink">
        You’re signed in as <strong dir="auto">{session.user.name}</strong> ({session.user.email}) to help them.
        Everything you do here is recorded against your own account too.
      </p>
      <Button size="sm" loading={returning} onClick={() => void returnToOwnAccount()}>
        Return to my account
      </Button>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  back,
  badges,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Where "back" goes from a detail or form page. Every one of them needs a way
   * out that is not the browser button, and putting it here puts it in the
   * same place on every screen.
   */
  back?: { to: string; label: string };
  /** Status chips that belong beside the title, such as a record's state. */
  badges?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="shrink-0 border-b border-line bg-surface px-4 py-3 sm:px-6">
      {back && (
        <Link
          to={back.to}
          className="mb-1 inline-flex items-center gap-1 rounded text-mini text-ink-subtle transition-colors hover:text-ink"
        >
          <ArrowLeft aria-hidden className="size-3 rtl:rotate-180" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {/*
              dir="auto" here rather than on each caller: this is every screen's
              heading, and several of them are the name of a record somebody
              typed — a catalogue item called اعطال لوحات الدجيتل, an asset, a
              knowledge article. Without it the first strong character never gets
              a say and the title renders left-to-right regardless.
            */}
            <h1 dir="auto" className="text-lg font-semibold tracking-tight text-ink">
              {title}
            </h1>
            {badges}
          </div>
          {description && (
            <p dir="auto" className="mt-0.5 text-body text-ink-muted">
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/** A page's code is still on its way: say so, centred where the page will be. */
function PageLoading() {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <Spinner label="Loading the page" />
    </div>
  );
}
